import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Application } from "@core/Application";
import { AnalyticsHelper } from "@core/Analytics/AnalyticsHelper";
import { QontactPointInformation } from "@models/QontactPoint";
import { Permissions } from "@models/Permissions";
import { useHasPermission } from "@hooks/useHasPermission";
import { ToastManager } from "@shared/designSystem/messages/ToastManager";
import {
    CollaboratorCustomField,
    QontactPointCustomFieldsService,
} from "@services/CollaboratorCustomFieldsService";
import {
    buildCustomFieldFormValues,
    getChangedCustomFieldValues,
    validateCustomFieldValues,
} from "@utils/CustomFieldValues";
import { validateLatitude, validateLongitude } from "@components/QontactPoints/CoordinatesFields";

// Same limit as the backend (QontactPointCreation.maxTriggerDistance)
export const maxTriggerDistanceInMeters = 10000;

interface FormErrors {
    name: string;
    latitude: string;
    longitude: string;
    triggerDistance: string;
}

const emptyErrors: FormErrors = { name: "", latitude: "", longitude: "", triggerDistance: "" };

const validateName = (value: string): string =>
    value.trim() ? "" : "%s es obligatorio".localized("Nombre".localized());

const validateTriggerDistance = (value: string): string => {
    if (!value.trim()) {
        return "%s es obligatorio".localized("Rango de fichada".localized());
    }
    const number = Number(value);
    if (!Number.isInteger(number) || number <= 0 || number > maxTriggerDistanceInMeters) {
        return "Ingresá un número de metros entre 1 y %s".localized(String(maxTriggerDistanceInMeters));
    }
    return "";
};

const getErrorMessage = (error: unknown): string | undefined => {
    if (error instanceof Error) {
        return error.message;
    }
    return typeof error === "string" ? error : undefined;
};

export const useQontactPointInformacionTabViewModel = (
    orgID: string,
    qontactPoint: QontactPointInformation,
    setQontactPoint: (qontactPoint: QontactPointInformation) => void,
) => {
    // Read-only only once we know the role lacks the permission (not while it loads);
    // the backend rejects writes without QP_MANAGE anyway.
    const canManage = useHasPermission(Permissions.QontactPoints.MANAGE) !== false;
    const customFieldsService = useMemo(
        () => new QontactPointCustomFieldsService(Application.shared().coreDependencies.httpClient),
        [],
    );

    // ---- Datos del lugar ----
    const [name, setName] = useState(qontactPoint.name ?? "");
    const [latitude, setLatitude] = useState(String(qontactPoint.latitude ?? ""));
    const [longitude, setLongitude] = useState(String(qontactPoint.longitude ?? ""));
    const [triggerDistance, setTriggerDistance] = useState(
        qontactPoint.triggerDistance != null ? String(qontactPoint.triggerDistance) : "",
    );
    const [coordinatesWereSplit, setCoordinatesWereSplit] = useState(false);
    const [errors, setErrors] = useState<FormErrors>(emptyErrors);
    const [isSaving, setIsSaving] = useState(false);

    // Reset the form only when another qontact point is opened. After a save the form
    // already holds the saved values, and after a partial failure it must keep what
    // the user typed for the fields that couldn't be saved.
    useEffect(() => {
        setName(qontactPoint.name ?? "");
        setLatitude(String(qontactPoint.latitude ?? ""));
        setLongitude(String(qontactPoint.longitude ?? ""));
        setTriggerDistance(qontactPoint.triggerDistance != null ? String(qontactPoint.triggerDistance) : "");
        setCoordinatesWereSplit(false);
        setErrors(emptyErrors);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qontactPoint.id]);

    // ---- Campos adicionales ----
    const [customFields, setCustomFields] = useState<CollaboratorCustomField[]>([]);
    const [storedCustomFieldValues, setStoredCustomFieldValues] = useState<Record<string, string>>({});
    const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
    const [customFieldErrors, setCustomFieldErrors] = useState<Record<string, string>>({});
    const [isLoadingCustomFields, setIsLoadingCustomFields] = useState(true);

    const initialCustomFieldValues = useMemo(
        () => buildCustomFieldFormValues(customFields, storedCustomFieldValues),
        [customFields, storedCustomFieldValues],
    );

    useEffect(() => {
        let isCancelled = false;
        const load = async () => {
            setIsLoadingCustomFields(true);
            try {
                const [fields, values] = await Promise.all([
                    customFieldsService.getFields(orgID),
                    customFieldsService.getValues(orgID, qontactPoint.id),
                ]);
                if (!isCancelled) {
                    setCustomFields(fields.filter((field) => field.isActive));
                    setStoredCustomFieldValues(values);
                }
            } catch (error) {
                if (!isCancelled) {
                    ToastManager.shared().showErrorMessage("No se pudieron cargar los campos adicionales".localized());
                }
            } finally {
                if (!isCancelled) {
                    setIsLoadingCustomFields(false);
                }
            }
        };
        load();
        return () => {
            isCancelled = true;
        };
    }, [customFieldsService, orgID, qontactPoint.id]);

    useEffect(() => {
        setCustomFieldValues(initialCustomFieldValues);
        setCustomFieldErrors({});
    }, [initialCustomFieldValues]);

    // ---- Analytics (same events as the previous "Editar" tab) ----
    const trackedShownIDRef = useRef<string | null>(null);
    const analyticsPayload = useCallback(() => ({
        qontactPointID: qontactPoint.id,
        buildingID: qontactPoint.buildingID,
        functionalUnitID: qontactPoint.functionalUnitID,
    }), [qontactPoint.buildingID, qontactPoint.functionalUnitID, qontactPoint.id]);

    useEffect(() => {
        if (!qontactPoint.id || trackedShownIDRef.current === qontactPoint.id) {
            return;
        }
        trackedShownIDRef.current = qontactPoint.id;
        AnalyticsHelper.trackQontactPointEditShown(analyticsPayload());
    }, [analyticsPayload, qontactPoint.id]);

    // ---- Handlers ----
    const handleNameChange = (value: string) => {
        setName(value);
        setErrors((current) => ({ ...current, name: "" }));
    };

    const handleLatitudeChange = (value: string) => {
        setLatitude(value);
        setCoordinatesWereSplit(false);
        setErrors((current) => ({ ...current, latitude: "" }));
    };

    const handleLongitudeChange = (value: string) => {
        setLongitude(value);
        setCoordinatesWereSplit(false);
        setErrors((current) => ({ ...current, longitude: "" }));
    };

    /** Click on the map: moves the point there (6 decimals, ~10 cm) */
    const handleMapPointSelected = (selectedLatitude: number, selectedLongitude: number) => {
        setLatitude(selectedLatitude.toFixed(6));
        setLongitude(selectedLongitude.toFixed(6));
        setCoordinatesWereSplit(false);
        setErrors((current) => ({ ...current, latitude: "", longitude: "" }));
    };

    const handleCoordinatesPairPasted = (pastedLatitude: string, pastedLongitude: string) => {
        setLatitude(pastedLatitude);
        setLongitude(pastedLongitude);
        setCoordinatesWereSplit(true);
        setErrors((current) => ({ ...current, latitude: "", longitude: "" }));
    };

    const handleTriggerDistanceChange = (value: string) => {
        setTriggerDistance(value.replace(/\D/g, ""));
        setErrors((current) => ({ ...current, triggerDistance: "" }));
    };

    const handleCustomFieldChange = (fieldID: string, value: string) => {
        setCustomFieldValues((current) => ({ ...current, [fieldID]: value }));
        setCustomFieldErrors((current) => ({ ...current, [fieldID]: "" }));
    };

    const trackCoordinatesHelpOpened = () => {
        AnalyticsHelper.trackQontactPointEditCoordinatesHelpOpened(analyticsPayload());
    };

    // ---- Save ----
    const handleSave = useCallback(async () => {
        if (isSaving || !canManage) {
            return;
        }

        const nextErrors: FormErrors = {
            name: validateName(name),
            latitude: validateLatitude(latitude),
            longitude: validateLongitude(longitude),
            triggerDistance: validateTriggerDistance(triggerDistance),
        };
        const nextCustomFieldErrors = validateCustomFieldValues(customFields, customFieldValues);
        const hasErrors =
            Object.values(nextErrors).some(Boolean) ||
            Object.values(nextCustomFieldErrors).some(Boolean);

        if (hasErrors) {
            setErrors(nextErrors);
            setCustomFieldErrors(nextCustomFieldErrors);
            ToastManager.shared().showErrorMessage("No se pudo guardar. Completá los campos obligatorios.".localized());
            return;
        }

        const nextLatitude = Number(latitude);
        const nextLongitude = Number(longitude);
        const nextName = name.trim();
        const buildingChanged =
            nextName !== (qontactPoint.name ?? "").trim() ||
            nextLatitude !== Number(qontactPoint.latitude) ||
            nextLongitude !== Number(qontactPoint.longitude);
        const nextTriggerDistance = triggerDistance.trim() ? Number(triggerDistance) : undefined;
        const triggerDistanceChanged =
            nextTriggerDistance !== undefined && nextTriggerDistance !== qontactPoint.triggerDistance;
        const changedCustomFieldValues = getChangedCustomFieldValues(
            customFields,
            initialCustomFieldValues,
            customFieldValues,
        );
        const customFieldsChanged = Object.keys(changedCustomFieldValues).length > 0;

        if (!buildingChanged && !triggerDistanceChanged && !customFieldsChanged) {
            ToastManager.shared().showInfoMessage("No se realizaron cambios".localized());
            return;
        }

        const saveAnalyticsPayload = {
            ...analyticsPayload(),
            changedName: nextName !== (qontactPoint.name ?? "").trim(),
            changedCoordinates:
                nextLatitude !== Number(qontactPoint.latitude) ||
                nextLongitude !== Number(qontactPoint.longitude),
        };

        setIsSaving(true);
        AnalyticsHelper.trackQontactPointEditSaveSubmitted(saveAnalyticsPayload);
        const qontactPointsService = Application.shared().coreDependencies.qontactPointsService;
        let updatedQontactPoint = { ...qontactPoint };

        try {
            if (buildingChanged) {
                await qontactPointsService.updateQontactPoint(qontactPoint.buildingID, {
                    name: nextName,
                    latitude: nextLatitude,
                    longitude: nextLongitude,
                });
                updatedQontactPoint = {
                    ...updatedQontactPoint,
                    name: nextName,
                    latitude: nextLatitude,
                    longitude: nextLongitude,
                };
            }

            if (triggerDistanceChanged && nextTriggerDistance !== undefined) {
                await qontactPointsService.updateTriggerDistance(orgID, qontactPoint.id, nextTriggerDistance);
                updatedQontactPoint = { ...updatedQontactPoint, triggerDistance: nextTriggerDistance };
            }

            if (customFieldsChanged) {
                const savedValues = await customFieldsService.updateValues(
                    orgID,
                    qontactPoint.id,
                    changedCustomFieldValues,
                );
                setStoredCustomFieldValues(savedValues);
            }

            AnalyticsHelper.trackQontactPointEditSaveSucceeded(saveAnalyticsPayload);
            ToastManager.shared().showSuccessMessage("Qontact Point guardado".localized());
        } catch (error) {
            AnalyticsHelper.trackQontactPointEditSaveFailed({
                ...saveAnalyticsPayload,
                errorMessage: getErrorMessage(error),
            });
            ToastManager.shared().showErrorMessage("No se pudo guardar el Qontact Point".localized());
        } finally {
            // Keep whatever was saved before a possible failure
            if (
                updatedQontactPoint.name !== qontactPoint.name ||
                updatedQontactPoint.latitude !== qontactPoint.latitude ||
                updatedQontactPoint.longitude !== qontactPoint.longitude ||
                updatedQontactPoint.triggerDistance !== qontactPoint.triggerDistance
            ) {
                setQontactPoint(updatedQontactPoint);
            }
            setIsSaving(false);
        }
    }, [
        analyticsPayload, canManage, customFieldValues, customFields, customFieldsService,
        initialCustomFieldValues, isSaving, latitude, longitude, name, orgID, qontactPoint,
        setQontactPoint, triggerDistance,
    ]);

    // ---- Delete ----
    const deleteQontactPoint = useCallback(async () => {
        const qontactPointsService = Application.shared().coreDependencies.qontactPointsService;
        await qontactPointsService.deleteQontactPoint(qontactPoint.id);
    }, [qontactPoint.id]);

    return {
        canManage,
        name,
        latitude,
        longitude,
        triggerDistance,
        coordinatesWereSplit,
        errors,
        isSaving,
        customFields,
        storedCustomFieldValues,
        customFieldValues,
        customFieldErrors,
        isLoadingCustomFields,
        handleNameChange,
        handleLatitudeChange,
        handleLongitudeChange,
        handleCoordinatesPairPasted,
        handleMapPointSelected,
        handleTriggerDistanceChange,
        handleCustomFieldChange,
        trackCoordinatesHelpOpened,
        handleSave,
        deleteQontactPoint,
    };
};
